import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { useAuth } from '../../auth/AuthProvider';
import { db } from '../../firebaseConfig';
import { loadOnTimeSettings, normalizeSettings, saveOnTimeSettings, slugify } from '../lib/ontimeSettings';

const inputClass = 'w-full rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500';
const cardClass = 'rounded-3xl border border-white/10 bg-white/5 p-5';
const buttonClass = 'rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-100 hover:bg-cyan-500/15';

function StoreRow({ store, onChange, onRemove }) {
  return (
    <div className="grid gap-3 rounded-2xl border border-white/10 bg-[#08111d] p-4 lg:grid-cols-[2.2fr_0.7fr_0.7fr_0.7fr_auto_auto]">
      <input className={inputClass} value={store.name} placeholder="Store name" onChange={(e) => onChange({ ...store, name: e.target.value })} />
      <input className={inputClass} value={store.deliveryFee} placeholder="Fee" onChange={(e) => onChange({ ...store, deliveryFee: e.target.value })} />
      <input className={inputClass} value={store.prepMinutes} placeholder="Prep" onChange={(e) => onChange({ ...store, prepMinutes: e.target.value })} />
      <input className={inputClass} value={store.maxKm} placeholder="KM" onChange={(e) => onChange({ ...store, maxKm: e.target.value })} />
      <label className="flex items-center justify-center rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-3 text-sm text-slate-200 gap-2">
        <input type="checkbox" checked={store.active !== false} onChange={(e) => onChange({ ...store, active: e.target.checked })} /> On
      </label>
      <button type="button" className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" onClick={onRemove}>Archive</button>
      <div className="lg:col-span-6">
        <input className={inputClass} value={store.address || ''} placeholder="Store address" onChange={(e) => onChange({ ...store, address: e.target.value })} />
      </div>
    </div>
  );
}

function TeamRow({ member, stores, roleLabel, onChange, onRemove }) {
  return (
    <div className="grid gap-3 rounded-2xl border border-white/10 bg-[#08111d] p-4 lg:grid-cols-[1.2fr_1fr_1fr_1fr_auto_auto]">
      <input className={inputClass} value={member.name} placeholder={`${roleLabel} name`} onChange={(e) => onChange({ ...member, name: e.target.value })} />
      <input className={inputClass} value={member.phone || ''} placeholder="Phone" onChange={(e) => onChange({ ...member, phone: e.target.value })} />
      <select className={inputClass} value={member.store || ''} onChange={(e) => onChange({ ...member, store: e.target.value })}>
        <option value="">Select store / area</option>
        {stores.map((store) => <option key={store.id || store.name} value={store.name}>{store.name}</option>)}
      </select>
      <input className={inputClass} value={member.email || ''} placeholder="Email" onChange={(e) => onChange({ ...member, email: e.target.value })} />
      <label className="flex items-center justify-center rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-3 text-sm text-slate-200 gap-2">
        <input type="checkbox" checked={member.active !== false} onChange={(e) => onChange({ ...member, active: e.target.checked })} /> On
      </label>
      <button type="button" className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" onClick={onRemove}>Archive</button>
    </div>
  );
}

async function resolveOrgId(explicitOrgId, claims, email) {
  if (explicitOrgId) return String(explicitOrgId);
  if (claims?.orgId) return String(claims.orgId);
  const ownerEmail = String(email || '').trim().toLowerCase();
  if (!ownerEmail) return '';
  try {
    const q = query(collection(db, 'orgs'), where('ownerEmail', '==', ownerEmail), limit(1));
    const snap = await getDocs(q);
    if (!snap.empty) return snap.docs[0].id;
  } catch {}
  return '';
}

export default function OnTimeSettingsPage({ orgId: explicitOrgId, currentUser }) {
  const { claims, user } = useAuth();
  const [resolvedOrgId, setResolvedOrgId] = useState(() => (explicitOrgId || claims?.orgId || ''));
  const [settings, setSettings] = useState(() => normalizeSettings({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError('');
      const nextOrgId = await resolveOrgId(explicitOrgId, claims, currentUser?.email || user?.email || '');
      if (ignore) return;
      if (!nextOrgId) {
        setResolvedOrgId('');
        setError('Missing organization.');
        setLoading(false);
        return;
      }
      setResolvedOrgId(nextOrgId);
      try {
        const loaded = await loadOnTimeSettings(nextOrgId);
        if (!ignore) setSettings(normalizeSettings(loaded));
      } catch (err) {
        if (!ignore) setError(err?.message || 'Unable to load Link settings.');
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [explicitOrgId, claims?.orgId, currentUser?.email, user?.email]);

  const activeStores = useMemo(() => (settings.stores || []).filter((s) => s.active !== false), [settings.stores]);

  const updateListItem = (key, index, value) => {
    setSettings((prev) => ({ ...prev, [key]: prev[key].map((item, i) => (i === index ? value : item)) }));
  };

  const addStore = () => setSettings((prev) => ({
    ...prev,
    stores: [...(prev.stores || []), { id: `store-${Date.now()}`, name: '', deliveryFee: prev.defaultDeliveryFee || 55, prepMinutes: prev.defaultPrepMinutes || 10, maxKm: 12, active: true, address: '' }],
  }));

  const addMember = (key, role) => setSettings((prev) => ({
    ...prev,
    [key]: [...(prev[key] || []), { id: `${role}-${Date.now()}`, name: '', phone: '', store: '', email: '', active: true, role }],
  }));

  const addSource = () => setSettings((prev) => ({ ...prev, sources: [...(prev.sources || []), ''] }));
  const addPayment = () => setSettings((prev) => ({ ...prev, paymentMethods: [...(prev.paymentMethods || []), { code: '', label: '' }] }));

  const handleSave = async () => {
    if (!resolvedOrgId) {
      setError('Missing organization.');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const payload = {
        ...settings,
        stores: (settings.stores || []).filter((store) => String(store.name || '').trim()).map((store) => ({
          ...store,
          id: store.id || slugify(store.name),
        })),
        onlineTeam: (settings.onlineTeam || []).filter((m) => String(m.name || '').trim()),
        drivers: (settings.drivers || []).filter((m) => String(m.name || '').trim()),
        sources: (settings.sources || []).map((s) => String(s || '').trim()).filter(Boolean),
        paymentMethods: (settings.paymentMethods || []).map((item) => ({
          code: String(item.code || slugify(item.label || '')).replace(/-/g, '_'),
          label: String(item.label || '').trim(),
        })).filter((item) => item.label),
      };
      const actor = currentUser?.email || user?.email || currentUser?.uid || user?.uid || 'unknown';
      await saveOnTimeSettings(resolvedOrgId, payload, actor);
      setSettings(normalizeSettings(payload));
      setMessage('Link settings saved.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.message || 'Unable to save Link settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-300">Loading Link settings...</div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 text-white">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Settings</h1>
          <p className="mt-2 text-slate-400">Manage stores, targets, online team, drivers, sources and card theme.</p>
        </div>
        <button type="button" onClick={handleSave} disabled={saving} className="rounded-3xl border border-emerald-400/25 bg-emerald-500/15 px-6 py-3 text-sm font-semibold text-emerald-100 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Link settings + team'}
        </button>
      </div>

      {message ? <div className="mb-4 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{message}</div> : null}
      {error ? <div className="mb-4 rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{error}</div> : null}

      <div className="grid gap-6 xl:grid-cols-[2.1fr_0.9fr]">
        <div className="space-y-6">
          <section className={cardClass}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Stores</div>
                <p className="mt-2 text-sm text-slate-400">Set delivery fee, prep minutes and max km per store.</p>
              </div>
              <button type="button" className={buttonClass} onClick={addStore}>+ Add store</button>
            </div>
            <div className="mb-3 hidden gap-3 px-1 text-xs uppercase tracking-[0.22em] text-slate-500 lg:grid lg:grid-cols-[2.2fr_0.7fr_0.7fr_0.7fr_auto_auto]">
              <div>Store name</div><div>Fee</div><div>Prep</div><div>KM</div><div>Status</div><div>Archive</div>
            </div>
            <div className="space-y-3">
              {(settings.stores || []).map((store, index) => (
                <StoreRow
                  key={store.id || index}
                  store={store}
                  onChange={(value) => updateListItem('stores', index, value)}
                  onRemove={() => setSettings((prev) => ({ ...prev, stores: prev.stores.filter((_, i) => i !== index) }))}
                />
              ))}
            </div>
          </section>

          <section className={cardClass}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Online team</div>
                <p className="mt-2 text-sm text-slate-400">These users capture, route and hand off orders.</p>
              </div>
              <button type="button" className={buttonClass} onClick={() => addMember('onlineTeam', 'online')}>+ Add online user</button>
            </div>
            <div className="space-y-3">
              {(settings.onlineTeam || []).map((member, index) => (
                <TeamRow key={member.id || index} member={member} stores={activeStores} roleLabel="Online" onChange={(value) => updateListItem('onlineTeam', index, value)} onRemove={() => setSettings((prev) => ({ ...prev, onlineTeam: prev.onlineTeam.filter((_, i) => i !== index) }))} />
              ))}
              {!(settings.onlineTeam || []).length ? <div className="text-sm text-slate-500">No online team created yet.</div> : null}
            </div>
          </section>

          <section className={cardClass}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Drivers</div>
                <p className="mt-2 text-sm text-slate-400">Drivers receive assigned runs and feed delivery reports.</p>
              </div>
              <button type="button" className={buttonClass} onClick={() => addMember('drivers', 'driver')}>+ Add driver</button>
            </div>
            <div className="space-y-3">
              {(settings.drivers || []).map((member, index) => (
                <TeamRow key={member.id || index} member={member} stores={activeStores} roleLabel="Driver" onChange={(value) => updateListItem('drivers', index, value)} onRemove={() => setSettings((prev) => ({ ...prev, drivers: prev.drivers.filter((_, i) => i !== index) }))} />
              ))}
              {!(settings.drivers || []).length ? <div className="text-sm text-slate-500">No drivers created yet.</div> : null}
            </div>
          </section>

          <section className={cardClass}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Order sources</div>
                <p className="mt-2 text-sm text-slate-400">Sources used by Intake.</p>
              </div>
              <button type="button" className={buttonClass} onClick={addSource}>+ Add source</button>
            </div>
            <div className="space-y-3">
              {(settings.sources || []).map((source, index) => (
                <div key={`${source}-${index}`} className="flex gap-3">
                  <input className={inputClass} value={source} onChange={(e) => setSettings((prev) => ({ ...prev, sources: prev.sources.map((item, i) => i === index ? e.target.value : item) }))} />
                  <button type="button" className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" onClick={() => setSettings((prev) => ({ ...prev, sources: prev.sources.filter((_, i) => i !== index) }))}>Archive</button>
                </div>
              ))}
            </div>
          </section>

          <section className={cardClass}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Payment methods</div>
                <p className="mt-2 text-sm text-slate-400">Methods shown in Intake and cards.</p>
              </div>
              <button type="button" className={buttonClass} onClick={addPayment}>+ Add payment method</button>
            </div>
            <div className="space-y-3">
              {(settings.paymentMethods || []).map((method, index) => (
                <div key={`${method.code || 'pm'}-${index}`} className="grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
                  <input className={inputClass} value={method.label || ''} placeholder="Label" onChange={(e) => setSettings((prev) => ({ ...prev, paymentMethods: prev.paymentMethods.map((item, i) => i === index ? { ...item, label: e.target.value } : item) }))} />
                  <input className={inputClass} value={method.code || ''} placeholder="Code" onChange={(e) => setSettings((prev) => ({ ...prev, paymentMethods: prev.paymentMethods.map((item, i) => i === index ? { ...item, code: e.target.value } : item) }))} />
                  <button type="button" className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" onClick={() => setSettings((prev) => ({ ...prev, paymentMethods: prev.paymentMethods.filter((_, i) => i !== index) }))}>Archive</button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className={cardClass}>
            <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Operational targets</div>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-2 block text-sm text-slate-300">Delivery target minutes</label>
                <input className={inputClass} value={settings.deliveryTargetMinutes || 60} onChange={(e) => setSettings((prev) => ({ ...prev, deliveryTargetMinutes: e.target.value }))} />
              </div>
              <div>
                <label className="mb-2 block text-sm text-slate-300">Online review target minutes</label>
                <input className={inputClass} value={settings.onlineTargetMinutes || 10} onChange={(e) => setSettings((prev) => ({ ...prev, onlineTargetMinutes: e.target.value }))} />
              </div>
              <div>
                <label className="mb-2 block text-sm text-slate-300">Default delivery fee</label>
                <input className={inputClass} value={settings.defaultDeliveryFee || 55} onChange={(e) => setSettings((prev) => ({ ...prev, defaultDeliveryFee: e.target.value }))} />
              </div>
              <div>
                <label className="mb-2 block text-sm text-slate-300">Default prep minutes</label>
                <input className={inputClass} value={settings.defaultPrepMinutes || 10} onChange={(e) => setSettings((prev) => ({ ...prev, defaultPrepMinutes: e.target.value }))} />
              </div>
            </div>
          </section>

          <section className={cardClass}>
            <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Order card theme</div>
            <div className="mt-4 grid gap-3">
              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-[#08111d] px-4 py-3 text-sm text-slate-200">
                <input type="radio" name="cardTheme" checked={(settings.storeHandoffCardTheme || 'dark') === 'dark'} onChange={() => setSettings((prev) => ({ ...prev, storeHandoffCardTheme: 'dark' }))} /> Premium navy card
              </label>
              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-[#08111d] px-4 py-3 text-sm text-slate-200">
                <input type="radio" name="cardTheme" checked={(settings.storeHandoffCardTheme || 'dark') === 'light'} onChange={() => setSettings((prev) => ({ ...prev, storeHandoffCardTheme: 'light' }))} /> Clean light card
              </label>
            </div>
          </section>

          <section className={cardClass}>
            <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Live setup summary</div>
            <div className="mt-4 space-y-3 text-sm text-slate-300">
              <div className="flex justify-between"><span>Active stores</span><span>{activeStores.length}</span></div>
              <div className="flex justify-between"><span>Active online team</span><span>{(settings.onlineTeam || []).filter((m) => m.active !== false).length}</span></div>
              <div className="flex justify-between"><span>Active drivers</span><span>{(settings.drivers || []).filter((m) => m.active !== false).length}</span></div>
              <div className="flex justify-between"><span>Delivery target</span><span>{settings.deliveryTargetMinutes || 60} min</span></div>
              <div className="flex justify-between"><span>Card theme</span><span>{(settings.storeHandoffCardTheme || 'dark') === 'light' ? 'Light' : 'Dark'}</span></div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
